import {
  BREAK_TIME_REGEX,
  GLOBAL_VOICE_FEATURES,
  NON_VBEE_VOICE_PROVIDERS,
  VIETNAM_VOICE_FEATURES,
} from '@src/constants/voice';
import { WALLET_TYPES } from '@src/constants';

const removeEmptyLineOfText = (text) => {
  const EMPTY_LINE = /^\s*\n/gm;
  const stringRemoveEmptyLine = text.trim().replace(EMPTY_LINE, '');
  return stringRemoveEmptyLine;
};

const handleDownload = async (url) => {
  // TODO: pass name variable
  if (!url) return;
  const link = document.createElement('a');
  link.href = url;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const countTextLengthOfSentences = (sentences) => {
  const contentLength = sentences.reduce(
    (sum, item) => (item.text ? sum + item.text.trim().length : sum),
    0,
  );
  return contentLength;
};

const countTextLength = (text, regex) => {
  const checkRegex = regex || BREAK_TIME_REGEX;
  const normalizeText = text.trim().replace(checkRegex, '');
  const textLength = normalizeText.length;
  return textLength;
};

const countCreditsSynthesis = ({ text = '', regex, voice }) => {
  const textLength = countTextLength(text, regex);
  const creditFactor = voice?.creditFactor || 1;
  return textLength * creditFactor;
};

// DEPRECATED
const countCreditsOfSentences = ({ sentences, nonVbeeCreditsFactor = 1 }) => {
  const contentLength = sentences.reduce((sum, item) => {
    const factor = NON_VBEE_VOICE_PROVIDERS.includes(item?.voice?.provider)
      ? nonVbeeCreditsFactor
      : 1;
    return item.text ? sum + item.text.trim().length * factor : sum;
  }, 0);

  return contentLength;
};

const checkFeaturePermission = (userFeatures = [], feature = '') => {
  const enabledMigrateFeatures = false;
  let migrateUserFeatures = userFeatures;

  // TODO: Remove after migration
  if (enabledMigrateFeatures) {
    if (migrateUserFeatures.includes(GLOBAL_VOICE_FEATURES.GLOBAL))
      migrateUserFeatures = [
        ...migrateUserFeatures,
        ...Object.values(GLOBAL_VOICE_FEATURES),
      ];

    if (migrateUserFeatures.includes(VIETNAM_VOICE_FEATURES.BASIC))
      migrateUserFeatures = [
        ...migrateUserFeatures,
        VIETNAM_VOICE_FEATURES.STANDARD,
      ];

    if (migrateUserFeatures.includes(VIETNAM_VOICE_FEATURES.PRO))
      migrateUserFeatures = [
        ...migrateUserFeatures,
        VIETNAM_VOICE_FEATURES.PREMIUM,
      ];

    if (migrateUserFeatures.includes(VIETNAM_VOICE_FEATURES.STANDARD))
      migrateUserFeatures = [
        ...migrateUserFeatures,
        VIETNAM_VOICE_FEATURES.BASIC,
      ];

    if (migrateUserFeatures.includes(VIETNAM_VOICE_FEATURES.PREMIUM))
      migrateUserFeatures = [
        ...migrateUserFeatures,
        VIETNAM_VOICE_FEATURES.ADVANCED,
      ];

    if (migrateUserFeatures.includes(GLOBAL_VOICE_FEATURES.STANDARD))
      migrateUserFeatures = [
        ...migrateUserFeatures,
        GLOBAL_VOICE_FEATURES.BASIC,
      ];

    if (migrateUserFeatures.includes(GLOBAL_VOICE_FEATURES.PREMIUM))
      migrateUserFeatures = [
        ...migrateUserFeatures,
        GLOBAL_VOICE_FEATURES.ADVANCED,
      ];
  }

  return migrateUserFeatures.includes(feature);
};

const checkValidXml = (xml) => {
  const oParser = new DOMParser();
  const oDOM = oParser.parseFromString(xml, 'application/xml');
  const isInvalidXML =
    oDOM.documentElement.getElementsByTagName('parsererror').length;
  return !isInvalidXML;
};

const getUserUpdateCredits = ({ ttsUser, user, credits, blockedCredits }) => {
  const {
    remainingCharacters: oneTimeRemainingCredits = 0,
    bonusCharacters: oneTimeBonusCredits = 0,
  } = user;
  const { studio: { cycle = {}, topUp = {}, custom = {} } = {} } = ttsUser;

  let remainingCredits = credits;

  const updateCredits = (currentCredits) => {
    const deducted = Math.min(remainingCredits, currentCredits);
    remainingCredits -= deducted;
    return currentCredits - deducted;
  };

  const remainingCycleCredits = cycle.remainingCredits || 0;
  const remainingTopUpCredits = topUp.remainingCredits || 0;
  const remainingCustomCredits = custom.remainingCredits || 0;
  let updatedCycleCredits = remainingCycleCredits;
  let updatedTopUpCredits = remainingTopUpCredits;
  let updatedCustomCredits = remainingCustomCredits;
  let updatedOneTimeCredits = oneTimeBonusCredits;
  let updatedOneTimeRemainingCredits = oneTimeRemainingCredits;

  const isLockCycleCredits = blockedCredits?.cycleCredits;
  const isLockTopUpCredits = blockedCredits?.topUpCredits;
  const isLockOneTimeCredits = blockedCredits?.oneTimeCredits;
  const isLockCustomCredits = blockedCredits?.customCredits;

  // Spend characters: topUp -> cycle -> oneTime (bonus -> remaining)
  if (!isLockTopUpCredits)
    updatedTopUpCredits = updateCredits(remainingTopUpCredits);
  if (!isLockCycleCredits)
    updatedCycleCredits = updateCredits(remainingCycleCredits);
  if (!isLockOneTimeCredits) {
    updatedOneTimeCredits = updateCredits(oneTimeBonusCredits);
    updatedOneTimeRemainingCredits = updateCredits(oneTimeRemainingCredits);
  }

  if (!isLockCustomCredits)
    updatedCustomCredits = updateCredits(remainingCustomCredits);

  return {
    'studio.cycle.remainingCredits': updatedCycleCredits,
    'studio.topUp.remainingCredits': updatedTopUpCredits,
    'studio.custom.remainingCredits': updatedCustomCredits,
    bonusCharacters: updatedOneTimeCredits,
    remainingCharacters: updatedOneTimeRemainingCredits,
  };
};

const calCredits = (currentCredits, spentCredits) => {
  const deducted = Math.min(spentCredits, currentCredits);
  const remainingSpentCredits = spentCredits - deducted;
  return {
    credits: currentCredits - deducted,
    spentCredits: remainingSpentCredits,
  };
};

const processWallets = ({
  usageWallets,
  currWallets,
  spentCredits: initialSpentCredits,
}) => {
  let { onetimeCredits, cycleCredits, topUpCredits, customCredits } =
    currWallets;
  let remainingSpentCredits = initialSpentCredits;

  // eslint-disable-next-line no-restricted-syntax
  for (const walletType of usageWallets) {
    switch (walletType) {
      case WALLET_TYPES.STUDIO_ONE_TIME: {
        const updated = calCredits(onetimeCredits, remainingSpentCredits);
        onetimeCredits = updated.credits;
        remainingSpentCredits = updated.spentCredits;
        break;
      }
      case WALLET_TYPES.STUDIO_CYCLE: {
        const updatedCycleCredits = calCredits(
          cycleCredits,
          remainingSpentCredits,
        );
        cycleCredits = updatedCycleCredits.credits;
        remainingSpentCredits = updatedCycleCredits.spentCredits;
        break;
      }
      case WALLET_TYPES.STUDIO_TOP_UP: {
        const updatedTopUpCredits = calCredits(
          topUpCredits,
          remainingSpentCredits,
        );
        topUpCredits = updatedTopUpCredits.credits;
        remainingSpentCredits = updatedTopUpCredits.spentCredits;
        break;
      }
      case WALLET_TYPES.STUDIO_CUSTOM: {
        const updatedCustomCredits = calCredits(
          customCredits,
          remainingSpentCredits,
        );
        customCredits = updatedCustomCredits.credits;
        remainingSpentCredits = updatedCustomCredits.spentCredits;
        break;
      }
      default:
        break;
    }
  }

  return {
    onetimeCredits,
    cycleCredits,
    topUpCredits,
    customCredits,
    spentCredits: remainingSpentCredits,
  };
};

const processWalletCreditsDeduction = ({
  sentencesCreditsInfo,
  currWallets,
}) => {
  let { onetimeCredits, cycleCredits, topUpCredits, customCredits } =
    currWallets;

  // eslint-disable-next-line no-restricted-syntax
  for (const sentence of sentencesCreditsInfo) {
    const { wallets, credits } = sentence;

    const result = processWallets({
      usageWallets: wallets,
      currWallets: {
        onetimeCredits,
        cycleCredits,
        topUpCredits,
        customCredits,
      },
      spentCredits: credits,
    });

    if (result.spentCredits > 0) return false;

    // Update credits for next iteration
    onetimeCredits = result.onetimeCredits;
    cycleCredits = result.cycleCredits;
    topUpCredits = result.topUpCredits;
    customCredits = result.customCredits;
  }

  return {
    onetimeCredits,
    cycleCredits,
    topUpCredits,
    customCredits,
  };
};

const getUserUpdateCreditsProcessWallet = ({
  sentencesCreditsInfo,
  currWallets,
  isFreeUser,
}) => {
  const updatedWallets = processWalletCreditsDeduction({
    sentencesCreditsInfo,
    currWallets,
  });
  if (!updatedWallets) return false; // Not enough credits

  const userUpdateFields = {};
  if (isFreeUser)
    userUpdateFields.bonusCharacters = updatedWallets.onetimeCredits;
  else userUpdateFields.remainingCharacters = updatedWallets.onetimeCredits;

  if (currWallets.cycleCredits)
    userUpdateFields['studio.cycle.remainingCredits'] =
      updatedWallets.cycleCredits;
  if (currWallets.topUpCredits)
    userUpdateFields['studio.topUp.remainingCredits'] =
      updatedWallets.topUpCredits;

  if (currWallets.customCredits)
    userUpdateFields['studio.custom.remainingCredits'] =
      updatedWallets.customCredits;

  return userUpdateFields;
};

export {
  handleDownload,
  countTextLengthOfSentences,
  countTextLength,
  countCreditsSynthesis,
  countCreditsOfSentences,
  checkFeaturePermission,
  checkValidXml,
  removeEmptyLineOfText,
  getUserUpdateCredits,
  getUserUpdateCreditsProcessWallet,
};
